// ============================================================================
// 경제 — 지갑 / 팩 구매 / 중복 판매 / (임시) 광고 보상
//
// **돈은 센트 정수다.** $0.05 를 계속 더하면 부동소수 오차가 쌓여 소지금이 $12.340000000000002
// 같은 값이 된다. 달러는 params.ts 에만 있고, 게임 안으로 들어오는 순간 센트로 바꾼다.
//
// 이 파일은 three.js 도 audio 도 모른다 — 순수 계산 + save.ts 의 저장 두 함수뿐이라
// 브라우저 없이 tools/econ-stats.mjs 가 그대로 import 한다.
//
// 판매 규칙: **중복만 판다. 각 종류의 마지막 1장은 팔 수 없다.**
// 바인더는 도감이라 한 번 모은 칸이 판매로 다시 비면 수집이 무너진다.
// ============================================================================
import { P } from './params.ts';
import { cardById } from './cards.ts';
import type { BinderSaveV1 } from './save.ts';
import { saveBinder } from './save.ts';

/** 달러 → 센트. 반올림은 여기 한 곳에서만 한다. */
export function toCents(dollars: number): number {
  return Math.round((Number.isFinite(dollars) ? dollars : 0) * 100);
}

/**
 * 표기 형식: **`$` + 소수점 두 자리 + 천 단위 쉼표** (`$1,234.50`).
 * 판매가 최소 단위가 $0.05 라 두 자리는 반드시 필요하고, 자리를 생략하면
 * ($4.5 / $4.49) 처럼 자릿수가 들쭉날쭉해져 숫자가 흔들려 보인다.
 * toLocaleString 은 지역 설정에 따라 형식이 달라져 쓰지 않는다.
 */
export function fmtCents(c: number): string {
  const n = Math.max(0, Math.round(c));
  const d = String(Math.floor(n / 100));
  const cents = String(n % 100).padStart(2, '0');
  return `$${d.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${cents}`;
}

export function packPriceCents(): number {
  return toCents(P.econ.packPrice);
}

/** 등급별 판매가 (센트). 등급 인덱스는 CARD_KINDS 와 같다 (0 커먼 / 1 레어 / 2 AR / 3 UR). */
export function sellPriceCents(rank: number): number {
  const e = P.econ;
  switch (rank | 0) {
    case 1:
      return toCents(e.sellRare);
    case 2:
      return toCents(e.sellAr);
    case 3:
      return toCents(e.sellUr);
    default:
      return toCents(e.sellCommon);
  }
}

/** 그 카드에서 팔 수 있는 장수 (= 보유 − 1). 미보유·1장이면 0. */
export function sellableCount(s: BinderSaveV1, id: string): number {
  return Math.max(0, (s.cards[id] ?? 0) - 1);
}

/** 저장소 전체에서 지금 팔 수 있는 금액 합 (센트). 막다른 길 판정에 쓴다. */
export function sellableValue(s: BinderSaveV1): number {
  let v = 0;
  for (const id of Object.keys(s.cards)) {
    const n = sellableCount(s, id);
    if (n <= 0) continue;
    const d = cardById(id);
    // 레지스트리에 없는 id(옛 세트)는 등급을 모른다 — 팔 수 없는 것으로 친다
    if (!d) continue;
    v += n * sellPriceCents(d.rank);
  }
  return v;
}

/**
 * 중복 한 장 판매. 성공하면 받은 금액(센트), 못 팔면 null.
 * 카드 −1 과 돈 +N 이 **한 번의 저장**으로 같이 나간다 (save.ts 의 v2 주석 참고).
 */
export function sellOne(s: BinderSaveV1, id: string): number | null {
  if (sellableCount(s, id) <= 0) return null;
  const d = cardById(id);
  if (!d) return null;
  const price = sellPriceCents(d.rank);
  s.cards[id] -= 1;
  s.money += price;
  saveBinder(s);
  return price;
}

export function canAffordPack(s: BinderSaveV1): boolean {
  return s.money >= packPriceCents();
}

/**
 * 팩 하나 구매. 성공하면 true (돈이 빠지고 openPaid 가 선다).
 * 이미 값을 치른 팩이 있으면 아무 일도 하지 않고 true.
 */
export function buyPack(s: BinderSaveV1): boolean {
  if (s.openPaid) return true;
  const price = packPriceCents();
  if (s.money < price) return false;
  s.money -= price;
  s.openPaid = true;
  saveBinder(s);
  return true;
}

/** 개봉하던 팩을 버린다 (자르기 시작한 뒤의 리셋). **환불 없다** — 이유는 main.ts newPack() 주석. */
export function discardPack(s: BinderSaveV1): void {
  if (!s.openPaid) return;
  s.openPaid = false;
  saveBinder(s);
}

/** 8장을 다 넘겨 팩을 소비했다. 다음 팩은 다시 사야 한다. */
export function consumePack(s: BinderSaveV1): void {
  s.openPaid = false;
}

// ----------------------------------------------------------------------------
// 광고 — **임시다.** 진짜 광고 SDK 는 없고 누르면 즉시 지급한다.
// 실제 SDK(AdMob / IronSource 등)를 붙일 때 이 함수 안이 "광고 재생 완료 콜백"이 된다.
// 그때 필요한 것: 재생 실패·중도 이탈 처리, 하루 시청 한도, 서버 검증.
// 지금은 무한히 누를 수 있어서 **경제 밸런스 판정에 쓰면 안 된다** (econ-stats 는 광고를 안 센다).
// ----------------------------------------------------------------------------
export function adRewardCents(long: boolean): number {
  return toCents(long ? P.econ.adLongReward : P.econ.adReward);
}

export function grantAd(s: BinderSaveV1, long: boolean): number {
  const r = adRewardCents(long);
  s.money += r;
  saveBinder(s);
  return r;
}

/**
 * 막다른 길: **중복을 전부 팔아도** 팩 값에 못 미친다. 이 상태는 화면에 보여야 한다 —
 * 광고 말고는 회복 경로가 없기 때문이다.
 * (중복이 남아 있어도 합계가 모자라면 똑같이 막다른 길이다. 그래서 "중복 0" 이 아니라 합계로 본다.)
 */
export function isDeadEnd(s: BinderSaveV1): boolean {
  return !s.openPaid && s.money + sellableValue(s) < packPriceCents();
}
